import { RawDraftContentState } from "draft-js";
import {
  invalidateBroadcastDetailsQuery,
  useBroadcastDetailsQuery,
} from "entities/broadcasts/queries/useBroadcastDetailsQuery";
import { useCreateBroadcastQuery } from "entities/broadcasts/queries/useCreateBroadcastQuery";
import { useUpdateBroadcastQuery } from "entities/broadcasts/queries/useUpdateBroadcastQuery";
import {
  BroadcastDestinations,
  BroadcastMessageUpdate,
  BroadcastToCreate,
  Broadcast,
  Status,
  Target,
} from "entities/broadcasts/types";
import { Status as UserStatus } from "entities/users/types";
import { FC, PropsWithChildren, useEffect, useState } from "react";
import {
  invalidateImageFilesQueries,
  useImageFilesQueries,
} from "shared/queries/useImageFileQueries";
import { createContext } from "shared/utils/context";
import { convertDraftToMarkdown } from "shared/utils/convertDraftToMarkdown";
import { useBroadcastsPage } from "../../../context";
import { ActionBlock, BroadcastFile, BroadcastProps } from "./types";

const [Provider, useBroadcastDetails] = createContext<BroadcastProps>({
  name: "BroadcastDetailsContext",
  hookName: "useBroadcastDetails",
  providerName: "BroadcastDetailsProvider",
});

const BroadcastDetailsProvider: FC<PropsWithChildren> = ({ children }) => {
  const { broadcastDetailsDrawer, tableData } = useBroadcastsPage();
  const [imageFile, setImageFile] = useState<BroadcastFile>(null);
  const [imageShown, setImageShown] = useState(false);
  const [actionBlocks, setActionBlocks] = useState<ActionBlock[]>([]);
  const [editorContent, setEditorContent] =
    useState<RawDraftContentState | null>(null);
  const [markdownContent, setMarkdownContent] = useState<string | null>(null);
  const [scheduledAt, setScheduledAt] = useState<string>("");
  const [isSchedulingEnabled, setIsSchedulingEnabled] = useState(false);
  const [isReadOnlyMode, setIsReadOnlyMode] = useState(false);
  const [target, setTarget] = useState<Target>(Target.ALL);
  const [usersStatuses, setUsersStatuses] = useState<UserStatus[]>([]);
  const [broadcastStatus, setBroadcastStatus] = useState<Status | null>(null);
  const { data: broadcastDetails, isLoading: isLoadingBroadcastDetails } =
    useBroadcastDetailsQuery({
      broadcastId: broadcastDetailsDrawer.drawerDetails?.id ?? "",
    });

  useEffect(() => {
    setImageFile(imageFile);
    setImageShown(broadcastDetails?.imageShown ?? false);
    setActionBlocks(broadcastDetails?.message.actions ?? []);
    setScheduledAt(broadcastDetails?.scheduledAt ?? "");
    setBroadcastStatus(broadcastDetails?.status ?? null);
    setIsReadOnlyMode(broadcastDetails?.status === Status.Sent);
    setTarget(broadcastDetails?.target ?? Target.ALL);
    setUsersStatuses(broadcastDetails?.usersStatuses ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    broadcastDetails?.message.image,
    broadcastDetails?.message.actions,
    broadcastDetails?.message.text,
    broadcastDetails?.imageShown,
    broadcastDetails?.scheduledAt,
    broadcastDetails?.status,
    broadcastDetails?.target,
    broadcastDetails?.usersStatuses,
  ]);

  const { mutateAsync: onCreateBroadcast, isPending: isSubmittingBroadcast } =
    useCreateBroadcastQuery();

  const { mutateAsync: onUpdateBroadcast, isPending: isUpdatingBroadcast } =
    useUpdateBroadcastQuery();

  const queries = useImageFilesQueries({
    queries: [
      {
        queryKey: [
          "broadcast-image-file",
          (broadcastDetails?.id ?? "").toString(),
        ],
        path: broadcastDetails?.message.image,
        setImageFile: setImageFile,
      },
    ],
    enabled: Boolean(broadcastDetails?.id),
  });

  const onCloseDrawer = () => {
    setActionBlocks([]);
    setImageFile(null);
    setImageShown(false);
    setEditorContent(null);
    setMarkdownContent(null);
    setScheduledAt("");
    setIsSchedulingEnabled(false);
    setTarget(Target.ALL);
    setUsersStatuses([]);
    broadcastDetailsDrawer?.closeDrawer();
  };

  const isLoadingImageFile = queries.every((query) => query.isFetching);

  const onSaveBroadcastDetails = async (status?: Status) => {
    try {
      const formattedActionBlocks = actionBlocks
        .filter((block) => block.text && block.url)
        .map((block) => ({
          ...block,
          url: block.url.startsWith("https://")
            ? block.url
            : `https://${block.url}`,
        }));

      const messageData: BroadcastMessageUpdate = {
        text: markdownContent ?? "",
        editorContent: editorContent ?? ({} as RawDraftContentState),
      };

      if (imageFile instanceof File) {
        messageData.image = imageFile;
      }
      if (formattedActionBlocks.length > 0) {
        messageData.actions = formattedActionBlocks as [
          { text: string; url: string },
        ];
      }

      const broadcastData = {
        message: messageData,
        scheduledAt: scheduledAt ?? "",
        imageShown: Boolean(imageFile),
        status:
          status ?? (scheduledAt?.length ? Status.Scheduled : Status.Draft),
        target: target,
        usersStatuses: usersStatuses,
      };

      if (broadcastDetails?.id) {
        // Update existing broadcast
        await onUpdateBroadcast({
          broadcastId: broadcastDetails.id,
          broadcast: broadcastData,
        });

        onCloseDrawer();

        if (tableData.updateBroadcastOptimistically) {
          const optimisticUpdatedBroadcast = {
            ...broadcastDetails,
            ...broadcastData,
            id: broadcastDetails.id,
            updatedAt: new Date(),
            message: {
              ...broadcastDetails.message,
              text: broadcastData.message.text,
              editorContent: broadcastData.message.editorContent,
              actions: broadcastData.message.actions,
            },
          } as Broadcast;

          tableData.updateBroadcastOptimistically(optimisticUpdatedBroadcast);

          setTimeout(async () => {
            await invalidateImageFilesQueries([
              ["broadcast-image-file", broadcastDetails.id],
            ]);

            await invalidateBroadcastDetailsQuery(broadcastDetails.id);
          }, 1000);
        }
      } else {
        // Create new broadcast
        const createdBroadcast = await onCreateBroadcast({
          broadcast: {
            ...broadcastData,
            destination: BroadcastDestinations.Telegram,
          } as BroadcastToCreate,
        });

        onCloseDrawer();

        if (tableData.addBroadcastOptimistically && createdBroadcast?.id) {
          const broadcastWithId = {
            ...createdBroadcast,
            id: createdBroadcast.id,
            createdAt: new Date(),
            updatedAt: new Date(),
          };

          tableData.addBroadcastOptimistically(broadcastWithId);

          setTimeout(async () => {
            await invalidateImageFilesQueries([
              ["broadcast-image-file", createdBroadcast.id],
            ]);
          }, 1000);
        }
      }
    } catch (e) {
      console.error("Error saving broadcast:", e);
    }
  };

  const handleEditorChange = (content: RawDraftContentState) => {
    setEditorContent(content);
    const markdown = convertDraftToMarkdown(content);
    setMarkdownContent(markdown);
  };

  const isDisabledSubmitButton =
    !markdownContent?.length ||
    (isSchedulingEnabled && !scheduledAt?.length) ||
    (target === Target.SPECIFIC &&
      (!usersStatuses || usersStatuses.length === 0));

  const isDisabledDraftButton =
    !markdownContent?.length ||
    isSchedulingEnabled ||
    (target === Target.SPECIFIC &&
      (!usersStatuses || usersStatuses.length === 0));

  const value = {
    broadcastDetails,
    isLoadingBroadcastDetails,
    imageFile,
    scheduledAt,
    setScheduledAt,
    setImageFile,
    onSaveBroadcastDetails,
    isSubmittingBroadcast: isSubmittingBroadcast || isUpdatingBroadcast,
    isLoadingImageFile,
    imageShown,
    setImageShown,
    actionBlocks,
    editorContent: broadcastDetails?.message.editorContent ?? null,
    setActionBlocks,
    handleEditorChange,
    isDisabledSubmitButton,
    isDisabledDraftButton,
    isSchedulingEnabled,
    setIsSchedulingEnabled,
    broadcastStatus,
    isReadOnlyMode,
    target,
    setTarget,
    onCloseDrawer,
    usersStatuses,
    setUsersStatuses,
  };

  return <Provider value={value}>{children}</Provider>;
};

export { BroadcastDetailsProvider, useBroadcastDetails };
